import { useState } from 'react';

import { Icon, Tooltip } from '@components';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { PaymentOrderTermType } from '@types';
import { formatISODate } from '@utils/date';

import TermBreakdownTooltip, {
  shouldShowTermInstallmentAmountTooltip
} from './TermInstallmentBreakdownTooltip';

import theme from '../../../../../themes/colors';

const DATE_FMT = 'DD MMM YYYY' as const;

type InstallmentScheduleBlockProps = {
  rows: PaymentOrderTermType[];
  isDefaultCurrency: boolean;
  source: 'member-details' | 'transaction-history';
};

const termStatus = (row: PaymentOrderTermType) =>
  row.payment_status?.toLowerCase() || '';

const isPaidComplete = (row: PaymentOrderTermType) => {
  const status = termStatus(row);
  return status === 'success';
};

const isTermDisabled = (row: PaymentOrderTermType) =>
  termStatus(row) === 'disabled';

const isTermInactive = (row: PaymentOrderTermType) =>
  (row.status ?? '').toLowerCase() === 'inactive';

const disabledTermTooltipText = (row: PaymentOrderTermType) => {
  const raw = (row.disabled_at ?? '').trim();
  if (!raw) return 'Payment link disabled';
  return `Payment link disabled on ${formatISODate({
    date: raw,
    format: DATE_FMT
  })}`;
};

const nonZeroAmount = (v: unknown) => Number(v ?? 0) !== 0;

const isProcessing = (row: PaymentOrderTermType) =>
  termStatus(row) === 'processing';

const termDisplayName = (
  name: string | undefined,
  index: number,
  termNumber?: number
) => {
  const raw = (name ?? '').trim();
  if (raw) return raw;
  const displayTermNumber = termNumber ?? index + 1;
  return `Installment ${displayTermNumber}`;
};
const segmentConnectorClass = (row: PaymentOrderTermType) =>
  isPaidComplete(row) ? 'border-cofeeGreen7' : 'border-grey40';

const StatusIcon = ({ row }: { row: PaymentOrderTermType }) => {
  if (isTermDisabled(row)) {
    return (
      <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-grey10">
        <Icon
          name="installment-term-disabled"
          size="none"
          className="size-4 shrink-0"
        />
      </span>
    );
  }
  if (isPaidComplete(row)) {
    return <Icon name="stepper-completed-icon" className="size-6 shrink-0" />;
  }
  if (isProcessing(row)) {
    return (
      <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-yellowLite4">
        <Icon
          name="alert-circle"
          size="none"
          className="size-4 shrink-0"
          stroke={theme.yellow70}
        />
      </span>
    );
  }
  return (
    <Icon
      name="installment-future-hourglass"
      size="none"
      className="size-6 shrink-0"
    />
  );
};

const DueDateLine = ({
  date,
  paragraphClassName
}: {
  date: string;
  paragraphClassName: string;
}) => (
  <p className={paragraphClassName}>
    Due on {formatISODate({ date, format: DATE_FMT })}
  </p>
);

const TermTimelineColumn = ({
  row,
  index,
  rows
}: {
  row: PaymentOrderTermType;
  index: number;
  rows: PaymentOrderTermType[];
}) => (
  <div className="flex w-6 shrink-0 flex-col items-center self-stretch">
    {index > 0 ? (
      <div
        className={`h-4 w-0 shrink-0 border-l-2 border-dashed ${segmentConnectorClass(
          rows[index - 1]
        )}`}
      />
    ) : null}
    <StatusIcon row={row} />
    {index < rows.length - 1 ? (
      <div
        className={`mt-0 min-h-10 w-0 flex-1 border-l-2 border-dashed ${segmentConnectorClass(
          row
        )}`}
      />
    ) : null}
  </div>
);

const TermCardAmount = ({ row }: { row: PaymentOrderTermType }) => {
  if (isPaidComplete(row)) {
    return row.paid_on ? (
      <p className="mt-2 text-sm font-normal text-grey">
        Paid on {formatISODate({ date: row.paid_on, format: DATE_FMT })}
      </p>
    ) : null;
  }

  const status = termStatus(row);

  const showPaidPendingSplit =
    status === 'processing' &&
    nonZeroAmount(row.paid_amount) &&
    nonZeroAmount(row.pending_amount);

  if (
    !row.due_date &&
    status !== 'processing' &&
    status !== 'payment_order_pending'
  ) {
    return null;
  }

  return (
    <div className="space-y-1">
      {row.due_date && (
        <DueDateLine
          date={row.due_date}
          paragraphClassName={
            isTermDisabled(row)
              ? 'text-sm font-normal text-grey line-through'
              : 'text-sm font-normal text-grey'
          }
        />
      )}

      {showPaidPendingSplit && (
        <p className="flex flex-wrap items-center gap-x-2 text-xs">
          <span className="inline-flex items-center gap-0.5 font-medium text-cofeeGreen7">
            Paid:
            <CurrencyAmount
              value={Number(row.paid_amount ?? 0)}
              className="gap-0.25"
              symbolClass="!text-xs text-cofeeGreen7"
              valueClass="font-medium text-cofeeGreen7"
            />
          </span>
          <span className="text-grey40">|</span>
          <span className="inline-flex items-center gap-0.5 font-medium text-cofeeOrange6">
            Pending:
            <CurrencyAmount
              value={Number(row.pending_amount ?? 0)}
              className="gap-0.25"
              symbolClass="!text-xs text-cofeeOrange6"
              valueClass="font-medium text-cofeeOrange6"
            />
          </span>
        </p>
      )}
    </div>
  );
};

const TermCard = ({
  row,
  index,
  amountSymbolClass,
  isDefaultCurrency
}: {
  row: PaymentOrderTermType;
  index: number;
  amountSymbolClass: string;
  isDefaultCurrency: boolean;
}) => {
  const [isAmountHovered, setIsAmountHovered] = useState(false);
  const requestedTermTotal = Number(row.amount ?? 0);
  const disabled = isTermDisabled(row);
  const inactive = !disabled && isTermInactive(row);
  const rowClass = disabled
    ? 'font-normal text-cofeeNeutral8 line-through'
    : 'font-semibold text-content';
  const amountSymbolClassResolved = disabled
    ? `${amountSymbolClass} text-cofeeNeutral8 line-through`.trim()
    : amountSymbolClass;

  const amountDisplay = (
    <CurrencyAmount
      value={requestedTermTotal}
      className="justify-end gap-0.5"
      valueClass={rowClass}
      symbolClass={amountSymbolClassResolved}
    />
  );

  const showBreakdownTooltip = shouldShowTermInstallmentAmountTooltip(
    row,
    String(row?.amount ?? 0)
  );

  const disabledTooltipId = `installment-disabled-${index}-${
    row.term_number ?? index
  }`;
  const inactiveTooltipId = `installment-inactive-${index}-${
    row.term_number ?? index
  }`;
  const showInactiveTooltip =
    inactive && !(showBreakdownTooltip && isAmountHovered);
  let cardTooltipId: string | undefined;
  if (disabled) cardTooltipId = disabledTooltipId;
  else if (showInactiveTooltip) cardTooltipId = inactiveTooltipId;

  const cardInner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className={rowClass}>
          {termDisplayName(row.name, index, row.term_number)}
        </div>
        <div
          className="flex shrink-0 items-start gap-1.5"
          onMouseEnter={() => setIsAmountHovered(true)}
          onMouseLeave={() => setIsAmountHovered(false)}
        >
          {showBreakdownTooltip && !disabled ? (
            <TermBreakdownTooltip
              id={`installment-schedule-total-${index}`}
              row={row}
              isDefaultCurrency={isDefaultCurrency}
            >
              <span className="shrink-0 border-b border-dotted border-cofeeNeutral10">
                {amountDisplay}
              </span>
            </TermBreakdownTooltip>
          ) : (
            <span className="shrink-0">{amountDisplay}</span>
          )}
        </div>
      </div>
      <TermCardAmount row={row} />
    </>
  );

  return (
    <>
      <div
        className={`rounded-lg border border-grey40 p-4 ${
          disabled || inactive
            ? 'cursor-not-allowed bg-cofeeGreyBlue1'
            : 'bg-white'
        }`}
        data-tooltip-id={cardTooltipId}
      >
        {cardInner}
      </div>
      {disabled ? (
        <Tooltip
          id={disabledTooltipId}
          type="custom"
          variant="dark"
          place="top"
          className="!max-w-90"
        >
          <div className="flex items-start gap-2 p-1">
            <Icon name="alert-circle-red" size="medium" className="shrink-0" />
            <span className="text-sm font-normal">
              {disabledTermTooltipText(row)}
            </span>
          </div>
        </Tooltip>
      ) : null}
      {showInactiveTooltip ? (
        <Tooltip
          id={inactiveTooltipId}
          type="text"
          variant="dark"
          place="top"
          tooltipText="The term is inactive"
        />
      ) : null}
    </>
  );
};

const TermRow = ({
  row,
  index,
  rows,
  amountSymbolClass,
  isDefaultCurrency
}: {
  row: PaymentOrderTermType;
  index: number;
  rows: PaymentOrderTermType[];
  amountSymbolClass: string;
  isDefaultCurrency: boolean;
}) => (
  <div className="flex gap-3">
    <TermTimelineColumn row={row} index={index} rows={rows} />
    <div className="min-w-0 flex-1 pb-5">
      <TermCard
        row={row}
        index={index}
        amountSymbolClass={amountSymbolClass}
        isDefaultCurrency={isDefaultCurrency}
      />
    </div>
  </div>
);

const InstallmentScheduleBlock = ({
  rows,
  isDefaultCurrency,
  source
}: InstallmentScheduleBlockProps) => {
  if (!rows.length) return null;

  const amountSymbolClass = isDefaultCurrency ? '' : '!text-sm !font-medium';

  return (
    <div className="mt-4">
      {source === 'member-details' && (
        <div className="text-md mb-3 font-medium text-cofeeNeutral10">
          Installment schedule
        </div>
      )}
      <div className="flex flex-col">
        {rows.map((row, index) => (
          <TermRow
            key={`${row.term_id ?? ''}-${row.due_date}`}
            row={row}
            index={index}
            rows={rows}
            amountSymbolClass={amountSymbolClass}
            isDefaultCurrency={isDefaultCurrency}
          />
        ))}
      </div>
    </div>
  );
};

export default InstallmentScheduleBlock;
